import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import _ from 'lodash';
import VideoContainer from 'components/VideoContainer';
import { getLiveVideo, isVideoLive } from 'components/VideoPlayerGlobal/Helper';
import {
  getAuthorizingNetwork,
  getNetworkList,
  getRequiresAuth,
  isClip,
  isEpisode,
} from 'helpers/ContentHelper';
import { getLocation } from 'helpers/GeoLocationHelper';
import { helperGetHomeUrl } from 'helpers/RouteHelper';
import { COLLECTION_VIDEO_BANNER_IMAGE, getDynamicImage } from 'helpers/ImageHelper';
import {
  LIVE,
  PLAYER_TYPE_DEFAULT,
  getPlayerCsid,
  playerRemove,
  playerSetUserActive,
} from 'helpers/PlayerHelper';
import { getSessionId } from 'helpers/AuthHelper';
import {
  callSegmentPlayerEvent,
  fireFloodlightEvent,
  getSegmentLiveViewedVideo,
  getSegmentPodId,
  getSegmentReferrer,
  getSegmentSourceName,
  getSegmentSourceType,
  PageNames,
  SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
  SegmentPlayerStates,
  SegmentVideoEvents,
  trackPageReferrerAnalytics,
} from '@foxcorp/delta-web-analytics';
import { getVideoObjectImages, getVideoObjectImage } from 'helpers/VideoProcessorHelper';
import { VIDEO_TYPE_CLIP } from 'screens/Watch/WatchHelpers';
import { fetchBannerVideo, fetchUpNext, clearBannerVideo } from './Actions';
import Style from './BannerVideo.module.scss';

class BannerVideo extends Component {
  constructor(props) {
    super(props);
    this.state = {
      adPlaying: false,
      entitlementIssues: null,
      isAuthError: null,
      isAutoPlay: false,
      isFullscreen: false,
      playerLoading: true,
      segmentReferrer: null,
      showControls: true,
    };
    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { userViewedPlayerBanner: prevBannerViewed } = prevProps;
    const { userViewedPlayerBanner } = this.props;

    if (userViewedPlayerBanner && !prevBannerViewed) {
      const { accessToken, deviceId, fetchBannerVideo, video } = this.props;
      const url = _.get(video, 'livePlayerScreenUrl');
      if (url) fetchBannerVideo({ url, accessToken, deviceId });
    }
  }

  componentWillUnmount() {
    const { clearBannerVideo } = this.props;
    clearBannerVideo();
  }

  getAuthAnalyticsProps = () => {
    const { accessToken, account, anonymousAccount } = this.props;
    const sid = getSessionId(accessToken || account?.accessToken || anonymousAccount?.accessToken);
    return { auth_token_sid: sid };
  };

  floodlightEvent = mediaInfo => {
    const podId = _.get(this.contentProps, 'pod_id', '');
    const isLongForm = !isClip(_.get(mediaInfo, 'videoType', ''));
    fireFloodlightEvent({ mediaInfo, podId }, isLongForm);
  };

  getLiveVideoData() {
    const { video, nextVideos } = this.props;
    if (_.isEmpty(nextVideos) && isVideoLive(video)) {
      return video;
    }
    return getLiveVideo({ nextVideos });
  }

  getSegmentSourceData = () => {
    const video = this.getLiveVideoData();
    const { preauthorizedResources } = this.props;
    const { currentVideo, isAutoPlay: autoplay } = this.state;
    const location = getLocation(window.referrer);
    const path = _.get(location, 'pathname');
    const segmentSourceType = getSegmentSourceType({
      autoplay,
      homeUrl: helperGetHomeUrl(),
      path,
    });
    const data = getSegmentLiveViewedVideo({
      video,
      prevVideo: currentVideo,
      segmentSourceType,
      preauthorizedResources,
    });
    const segmentSourceName = getSegmentSourceName({
      data,
      type: segmentSourceType,
    });
    return {
      segmentSourceName,
      segmentSourceType,
    };
  };

  getUpNext(video) {
    const { accessToken, deviceId, fetchUpNext } = this.props;
    const videoId = _.get(video, 'id');
    if (videoId) fetchUpNext({ accessToken, videoId, deviceId });
  }

  getPlayerEvents = () => {
    const { playerReference } = this.props;
    return {
      onReady: contentVideoData => {
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
      },
      onAdLoadStart: () => {
        console.debug('!!live - onAdLoadStart');
      },
      onAdLoadComplete: () => {
        console.debug('!!live - onAdLoadComplete');
      },
      onAdPodStart: contentVideoData => {
        console.debug('!!live - onAdPodStart');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodStarted,
          props: data,
        });
        this.setState({ adPlaying: true });
      },
      onAdPodComplete: contentVideoData => {
        console.debug('!!live - onAdPodComplete');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodCompleted,
          props: data,
        });
        this.setState({ adPlaying: false });
      },
      onAdStart: contentVideoData => {
        console.debug('!!live - leapfrog onAdStart');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const props = {
          ...this.videoProps,
          ...eventVideoProps,
          ...this.adData,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdStarted,
          props,
        });
      },
      onAdPlaying: contentVideoData => {
        console.debug('!!live - leapfrog onAdPlaying');
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        if (this.newAdTime !== this.previousAdTime && this.newAdTime % this.AD_INTERVAL_SEC === 0) {
          const eventAdProps = _.get(contentVideoData, 'trackingData.ad', {});
          const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
          const data = {
            ...this.videoProps,
            ...this.adData,
            ...eventAdProps,
            ...eventVideoProps,
            video_seconds_viewed: this.newAdTime,
            ...this.getAuthAnalyticsProps(),
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoAdPlaying,
            props: data,
          });
        }
        this.previousAdTime = this.newAdTime;
      },
      onAdComplete: contentVideoData => {
        console.debug('!!live - leapfrog onAdComplete');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const props = {
          ...this.videoProps,
          ...this.adData,
          video_seconds_viewed: this.newAdTime,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdCompleted,
          props,
        });
      },
      onAdPause: contentVideoData => {
        console.debug('!!live - leapfrog onAdPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const adVideoProps = _.get(contentVideoData, 'trackingData.ad', {});
        const data = {
          ...this.videoProps,
          ...eventVideoProps,
          ...adVideoProps,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
      },
      onAdResume: contentVideoData => {
        console.debug('!!live - leapfrog onAdResume');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.eventProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.eventProps,
          ...this.adData,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdResumed,
          props: data,
        });
      },
      onBufferEnd: contentVideoData => {
        console.debug('!!live - leapfrog onBufferEnd');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...eventVideoProps,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferComplete,
          props: data,
        });
      },
      onBufferStart: contentVideoData => {
        console.debug('!!live - leapfrog onBufferStart');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...eventVideoProps,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferStarted,
          props: data,
        });
      },
      onChromecastStart: () => {
        this.floodlightEvent(this.getLiveVideoData());
      },
      onChromecastComplete: () => {
        this.floodlightEvent(this.getLiveVideoData());
      },
      onContentComplete: contentVideoData => {
        console.debug('!!live - leapfrog onContentComplete');
        const totalSecondsViewed = _.get(contentVideoData, 'secondsViewed');
        const secondsViewed = Math.floor(totalSecondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC);
        const completedPodId = getSegmentPodId(contentVideoData);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          secondsViewed,
          ...this.getAuthAnalyticsProps(),
        };
        this.setState({ completedPodId });
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentComplete,
          props: data,
        });
      },
      onContentPlaying: contentVideoData => {
        const { secondsViewed: previouslyViewed } = this.state;
        const secondsViewed = _.get(contentVideoData, 'secondsViewed', 0);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        if (
          secondsViewed !== previouslyViewed &&
          secondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC === 0
        ) {
          const data = {
            ...this.videoProps,
            ...this.contentProps,
            ...eventVideoProps,
            video_seconds_viewed: SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
            ...this.getAuthAnalyticsProps(),
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoContentPlaying,
            props: data,
          });
        }
        this.setState({ secondsViewed });
      },
      onContentStart: contentVideoData => {
        console.debug('!!live - leapfrog onContentStart');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentStart,
          props: data,
        });
        const { completedPodId } = this.state;
        if (completedPodId) this.setState({ completedPodId: null });
      },
      onFirstFrame: async () => {
        console.debug('!!live - onFirstFrame');
        const { segmentReferrer: prevSegmentReferrer } = this.state;
        const { segmentSourceType } = this.getSegmentSourceData();
        const segmentReferrer = getSegmentReferrer({
          segmentSourceType,
          prevSegmentReferrer,
        });
        this.setState({
          playerLoading: false,
          segmentReferrer,
        });
      },
      onFullScreen: e => {
        const { fullscreen } = e;
        this.setState({ isFullscreen: fullscreen });
        this.videoProps.video_is_fullscreen = fullscreen;
      },
      onMediaComplete: () => {
        console.debug('!!live - onMediaComplete');
        this.setState({ isAutoPlay: true });
      },
      onMediaPlaying: () => {
        // console.debug('!!live - onMediaPlaying');
      },
      onMediaStart: () => {
        console.debug('!!live - onMediaStart');
        const currentVideo = this.getLiveVideoData();
        this.floodlightEvent(currentVideo);
        const { updateBannerVideo } = this.props;
        const { currentVideo: prevCurrentVideo, segmentReferrer: prevSegmentReferrer } = this.state;
        const currentVideoId = _.get(currentVideo, 'id');
        const prevVideoId = _.get(prevCurrentVideo, 'id');
        if (currentVideoId !== prevVideoId) {
          this.setState({ currentVideo });
          if (prevCurrentVideo) updateBannerVideo(currentVideo);
        }
        const { segmentSourceType } = this.getSegmentSourceData();
        const segmentReferrer = getSegmentReferrer({
          segmentSourceType,
          prevSegmentReferrer,
        });
        this.getUpNext(currentVideo);
        this.setState(
          {
            playerLoading: false,
            segmentReferrer,
          },
          () => {
            const { isAutoPlay } = this.state;
            if (isAutoPlay) {
              this.trackPageReferrerAnalytics({
                name: PageNames.CONTINUOUS_PLAY,
              });
            }
          },
        );
      },
      onNielsenDtvrTag: contentVideoData => {
        console.debug('!!live - onNielsenDtvrTag');
        const nielsen_id3_tag = _.get(contentVideoData, 'dtvrTag', {});
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoNielsenTagDetected,
          props: {
            nielsen_id3_tag,
          },
        });
      },
      onPause: contentVideoData => {
        console.debug('!!live - leapfrog onPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...eventVideoProps,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
      },
      onPlaybackComplete: contentVideoData => {
        console.debug('!!live - leapfrog onPlaybackComplete');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...eventVideoProps,
          ...this.getAuthAnalyticsProps(),
        };

        data.video_player_content_type = null;
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackComplete,
          props: data,
        });
        this.setState({ completedPodId: null });
      },
      onPlaybackInterrupted: data => {
        console.debug('!!live - leapfrog onPlaybackInterrupted');
        const { method } = data;
        const props = {
          method,
          video_player_state: SegmentPlayerStates.PAUSE,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackInterrupted,
          props,
        });
      },
      onPlaybackStart: contentVideoData => {
        console.debug('!!live - leapfrog onPlaybackStart');
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          position: 0, // position is always 0 for playback start
          video_player_content_type: null,
          content_asset_id: _.get(contentVideoData, 'trackingData.content_asset_id'),
          content_pod_ids: _.get(contentVideoData, 'trackingData.content_pod_ids'),
          content_pod_id: null,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackStart,
          props: data,
        });
      },
      onPlayerLoadComplete: () => {
        const { isAuthError: lclIsAuthError } = this.state;
        this.setState({ playerLoading: false });
        if (lclIsAuthError) playerRemove(playerReference);
      },
      onResume: contentVideoData => {
        console.debug('!!live - leapfrog onResume');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...eventVideoProps,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackResumed,
          props: data,
        });
      },
      onShowControls: e => {
        const { showControls: oldShowControls, isAuthError: lclIsAuthError } = this.state;
        if (oldShowControls !== e && !lclIsAuthError) {
          this.setState({ showControls: e });
        }
      },
      onVolumeChange: ({ newVolume, oldVolume }) => {
        const { video_volume } = this.videoProps;
        if (video_volume === newVolume || oldVolume === newVolume) return null;
        this.videoProps.video_volume = newVolume;
        return null;
      },
    };
  };

  setStickyControls = sticky => {
    const { playerReference } = this.props;
    this.setState({ stickyControls: sticky }, () => {
      playerSetUserActive(playerReference, sticky);
    });
  };

  render() {
    const {
      adPlaying,
      entitlementIssues,
      isFullscreen,
      playerLoading,
      showControls,
      stickyControls,
    } = this.state;
    const {
      accessToken,
      authSuccess,
      breakpoint,
      deviceId,
      defaultVideoContainerProps,
      playerReference,
      reducerVideo,
      releaseUrl,
      userViewedPlayerBanner,
      video,
    } = this.props;

    const fetchedVideo = reducerVideo || video;
    const {
      callSign,
      contentRating,
      contentSKU,
      guid,
      id: videoId,
      images,
      livePlayerScreenUrl,
      network,
      networks,
      scrubbingEnabled,
      showCode,
      videoType,
    } = fetchedVideo;

    const containerId = `video-banner-${videoId}`;
    const liveContentId = _.last(_.split(livePlayerScreenUrl, '/'));
    const shouldShowControls = showControls || stickyControls;
    const siteSection = getPlayerCsid({
      showCode,
      isFullEpisode: isEpisode(videoType),
    });
    const playerType = PLAYER_TYPE_DEFAULT;
    const requiresAuth = getRequiresAuth({ video });
    const requiresAuthLive = getRequiresAuth({ video, isLive: true });
    const networkEntitlementList = getNetworkList();
    const authorizingNetwork = getAuthorizingNetwork(fetchedVideo);
    const contentNetworks = networks || [network];
    const isClip = videoType === VIDEO_TYPE_CLIP;
    const scrubbingEnabledCalc = scrubbingEnabled || isClip;
    const containerClass = Style.videoContainer;
    const shouldCheckPreviewPass = true;
    const disablePreviewPassRender = false;
    const imageObj = getVideoObjectImages({ images, defaultImage: 'seriesList' });
    const imageUrl = getVideoObjectImage({ image: imageObj });
    const background = getDynamicImage({
      breakpoint,
      context: COLLECTION_VIDEO_BANNER_IMAGE,
      url: imageUrl,
    });

    const videoContainerProps = {
      ...defaultVideoContainerProps,
      authorizingNetwork,
      authSuccess,
      backfade: (showControls || playerLoading) && !adPlaying,
      background,
      breakpoint,
      callSign,
      channel: callSign,
      containerId,
      containerClass,
      contentNetworks,
      contentRating,
      contentSKU,
      deviceId,
      entitlementIssues,
      guid,
      hideLoadingText: true,
      isFullscreen,
      isLive: true,
      isVideoBanner: true,
      liveContentId,
      mediaInfo: reducerVideo,
      mode: LIVE,
      mute: true,
      network,
      networkEntitlementList,
      playerConfig: this.getPlayerEvents(),
      playerLoading,
      playerLoadingImage: background,
      profileToken: accessToken,
      reducerVideo,
      releaseURL: releaseUrl,
      requiresAuth,
      requiresAuthLive,
      scrubbingEnabled: scrubbingEnabledCalc,
      selectedVideo: videoId,
      setAuthError: value => this.setState({ isAuthError: value }),
      setInterval,
      setStickyControls: this.setStickyControls,
      shouldCheckPreviewPass,
      disablePreviewPassRender,
      showControls: shouldShowControls,
      siteSection,
      theme: Style,
      type: playerType,
      videoId,
      videoType,
      waitToRenderPlayer: !userViewedPlayerBanner,
    };

    return (
      <div id={containerId} className={Style.imageContainer}>
        <VideoContainer {...videoContainerProps} playerReference={playerReference} />
      </div>
    );
  }
}

function mapStateToProps({ bannerVideo }) {
  return {
    errors: bannerVideo.errors,
    nextVideos: bannerVideo.nextVideos,
    releaseUrl: bannerVideo.releaseUrl,
    reducerVideo: bannerVideo.reducerVideo,
  };
}

export default connect(mapStateToProps, {
  clearBannerVideo,
  fetchBannerVideo,
  fetchUpNext,
})(BannerVideo);

BannerVideo.defaultProps = {
  accessToken: null,
  account: null,
  anonymousAccount: null,
  anonymousId: null,
  authSuccess: false,
  defaultVideoContainerProps: {},
  deviceId: null,
  errors: null,
  nextVideos: [],
  preauthorizedResources: [],
  reducerVideo: null,
  releaseUrl: null,
  updateBannerVideo: () => {},
  userViewedPlayerBanner: false,
};

BannerVideo.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func.isRequired,
  authSuccess: PropTypes.bool,
  breakpoint: PropTypes.string.isRequired,
  clearBannerVideo: PropTypes.func.isRequired,
  defaultVideoContainerProps: PropTypes.shape({}),
  deviceId: PropTypes.string,
  errors: PropTypes.number,
  fetchBannerVideo: PropTypes.func.isRequired,
  fetchUpNext: PropTypes.func.isRequired,
  nextVideos: PropTypes.arrayOf(PropTypes.shape({})),
  playerReference: PropTypes.string.isRequired,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  reducerVideo: PropTypes.shape({}),
  releaseUrl: PropTypes.string,
  updateBannerVideo: PropTypes.func,
  userViewedPlayerBanner: PropTypes.bool,
  video: PropTypes.shape({}).isRequired,
};
